import React, { useState, useEffect, useMemo } from "react";
import { FiSearch, FiRefreshCw, FiDownload, FiX, FiEye, FiCheckCircle, FiClipboard, FiFileText } from "react-icons/fi";
import "./staffcss/medicalappointments.css";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import { db, auth } from "../../firebase";
import { collection, query, where, orderBy, onSnapshot, limit } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import Banner from "./Banner";
import MedicalEscalationForm from "./MedicalEscalationForm";
import Portal from "../../components/Portal";

const STATUS_FILTER_TABS = [
  { label: "All", value: "all" },
  { label: "Pending Review", value: "pending-review" },
  { label: "Under Assessment", value: "under-assessment" },
  { label: "Resolved", value: "resolved" }
];

const TOPIC_OPTIONS = [
  "HIV Awareness",
  "HIV Screening/Testing",
  "Birth Control/Implant",
  "Contraception",
  "Family Planning",
  "Gender-Based Violence",
  "Menstrual Health & Hygiene"
];

const REASON_OPTIONS = [
  "Medical advice needed",
  "Possible symptoms or health concern",
  "Medication/treatment-related question",
  "Urgent concern",
  "Sensitive/personal situation",
  "Unable to provide sufficient information"
];

const getStatusLabel = (status) => {
  const mapping = {
    'pending-review': 'Pending Review',
    'under-assessment': 'Under Assessment',
    'resolved': 'Resolved'
  };
  return mapping[status] || status;
};

const getStatusBadgeClass = (status) => {
  return `escalation-status-${status}`;
};

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const AdminMedicalEscalatedCases = () => {
  const [escalatedCases, setEscalatedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [bannerMessage, setBannerMessage] = useState("");
  const bannerType = "error";
  const [selectedCase, setSelectedCase] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  // Fetch escalated chats with real-time updates and limit for efficiency
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Build query with server-side filtering and limit
    let q = query(
      collection(db, "escalatedChats"),
      orderBy("createdAt", "desc"),
      limit(100) // Limit to prevent excessive reads while maintaining real-time updates
    );

    // Add server-side status filter when not viewing "all"
    if (statusFilter !== "all") {
      q = query(
        collection(db, "escalatedChats"),
        where("escalationStatus", "==", statusFilter),
        orderBy("createdAt", "desc"),
        limit(100)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const casesData = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        casesData.push({
          id: doc.id,
          ...data
        });
      });

      setEscalatedCases(casesData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching escalated cases:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [statusFilter]);

  // Filter cases (status filtering is now server-side)
  const filteredCases = escalatedCases.filter(c => {
    const matchesSearch = !search || 
      c.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
      c.escalationTopic?.toLowerCase().includes(search.toLowerCase()) ||
      c.escalationReason?.toLowerCase().includes(search.toLowerCase());
    
    const matchesTopic = !topicFilter || c.escalationTopic === topicFilter;
    
    const matchesReason = !reasonFilter || c.escalationReason === reasonFilter;

    return matchesSearch && matchesTopic && matchesReason;
  });

  // Cache status tab counts (limited to current page with server-side pagination)
  const statusTabCounts = useMemo(() => {
    const counts = {
      'all': escalatedCases.length,
      'pending-review': 0,
      'under-assessment': 0,
      'resolved': 0
    };
    
    escalatedCases.forEach(c => {
      if (c.escalationStatus && counts[c.escalationStatus] !== undefined) {
        counts[c.escalationStatus]++;
      }
    });
    
    return counts;
  }, [escalatedCases]);

  const getStatusTabCount = (value) => statusTabCounts[value] || 0;

  // Client-side pagination
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  const handleRefresh = () => {
    // Real-time listener will automatically refresh
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  const handleExportPDF = () => {
    const pdf = createPDF({
      title: "Escalated Cases Report"
    });
    
    // Prepare data for table
    let headers = ['Status', 'Ticket Number', 'Topic', 'Reason', 'Date Escalated'];
    if (statusFilter === 'resolved') {
      headers = ['Status', 'Ticket Number', 'Topic', 'Reason', 'Date Escalated', 'Date Resolved'];
    }
    
    const rows = filteredCases.map(c => {
      const row = [
        getStatusLabel(c.escalationStatus),
        c.ticketNumber || "N/A",
        c.escalationTopic || "N/A",
        c.escalationReason || "N/A",
        c.createdAt ? new Date(c.createdAt.toDate()).toLocaleDateString() : "N/A"
      ];
      if (statusFilter === 'resolved') {
        row.push(c.resolvedAt ? new Date(c.resolvedAt.toDate()).toLocaleDateString() : "N/A");
      }
      return row;
    });
    
    pdf.autoTable({
      head: [headers],
      body: rows
    });
    
    pdf.save(`escalated-cases-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleExportExcel = () => {
    const worksheetData = [['Status', 'Ticket Number', 'Topic', 'Reason', 'Date Escalated']];
    if (statusFilter === 'resolved') {
      worksheetData[0].push('Date Resolved');
    }
    
    filteredCases.forEach(c => {
      const row = [
        getStatusLabel(c.escalationStatus),
        c.ticketNumber || "N/A",
        c.escalationTopic || "N/A",
        c.escalationReason || "N/A",
        c.createdAt ? new Date(c.createdAt.toDate()).toLocaleDateString() : "N/A"
      ];
      if (statusFilter === 'resolved') {
        row.push(c.resolvedAt ? new Date(c.resolvedAt.toDate()).toLocaleDateString() : "N/A");
      }
      worksheetData.push(row);
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Escalated Cases");
    XLSX.writeFile(workbook, `escalated-cases-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportCSV = () => {
    const headers = ['Status', 'Ticket Number', 'Topic', 'Reason', 'Date Escalated'];
    if (statusFilter === 'resolved') {
      headers.push('Date Resolved');
    }
    
    const csvRows = [headers.join(',')];
    
    filteredCases.forEach(c => {
      const row = [
        getStatusLabel(c.escalationStatus),
        c.ticketNumber || "N/A",
        c.escalationTopic || "N/A",
        c.escalationReason || "N/A",
        c.createdAt ? new Date(c.createdAt.toDate()).toLocaleDateString() : "N/A"
      ];
      if (statusFilter === 'resolved') {
        row.push(c.resolvedAt ? new Date(c.resolvedAt.toDate()).toLocaleDateString() : "N/A");
      }
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `escalated-cases-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportReport = (format) => {
    setIsExporting(true);
    try {
      if (format === 'pdf') {
        handleExportPDF();
      } else if (format === 'excel') {
        handleExportExcel();
      } else if (format === 'csv') {
        handleExportCSV();
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setTimeout(() => setIsExporting(false), 500);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit',
      minute: '2-digit'
    });
    return (
      <>
        <div>{dateStr}</div>
        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{timeStr}</div>
      </>
    );
  };

  return (
    <div className="account-management-container">
      {selectedCase ? (
        <MedicalEscalationForm
          caseData={selectedCase}
          onBack={() => setSelectedCase(null)}
          readOnly={selectedCase.escalationStatus === "resolved"}
          hideEscalationForm={selectedCase.escalationStatus === "under-assessment"}
        />
      ) : (
        <>
          {bannerMessage && (
            <Banner
              message={bannerMessage}
              type={bannerType}
              onClose={() => setBannerMessage("")}
            />
          )}

          {/* Header */}
          <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Escalated Cases
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Overview of all escalated cases from peer educator chats
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Filters */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            type="text"
            placeholder="Search inquiries..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>

        <select
          className="admin-aura-select"
          value={topicFilter}
          onChange={(e) => {
            setTopicFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Topics</option>
          {TOPIC_OPTIONS.map(topic => (
            <option key={topic} value={topic}>{topic}</option>
          ))}
        </select>

        <select
          className="admin-aura-select"
          value={reasonFilter}
          onChange={(e) => {
            setReasonFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Reasons</option>
          {REASON_OPTIONS.map(reason => (
            <option key={reason} value={reason}>{reason}</option>
          ))}
        </select>

        <button 
          onClick={handleRefresh}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 20px",
            borderRadius: "12px",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            background: "rgba(255, 255, 255, 0.9)",
            color: "#3b82f6",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          <FiRefreshCw />
          Refresh
        </button>
        <button 
          onClick={() => setShowExportModal(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 20px",
            borderRadius: "12px",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            background: "rgba(255, 255, 255, 0.9)",
            color: "#3b82f6",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          <FiDownload />
          Export Report
        </button>
      </div>

        {/* Status Tabs Row with Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter escalated cases by status" style={{ display: "flex", gap: "8px" }}>
          {STATUS_FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`floating-tab ${statusFilter === tab.value ? "active floating-tab-active" : ""}`}
              onClick={() => {
                setStatusFilter(tab.value);
                setCurrentPage(1);
              }}
            >
              {tab.label}
              <span className="tab-count">{getStatusTabCount(tab.value)}</span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div style={{ 
            display: "flex", 
            gap: "16px", 
            alignItems: "center" 
          }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {filteredCases.length > 0 ? (
                `${startIndex + 1}-${Math.min(endIndex, filteredCases.length)} of ${filteredCases.length}`
              ) : (
                '0 of 0'
              )}
            </div>

            {/* Pagination Arrows */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                ←
              </button>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ 
                  backgroundColor: '#1e3a8a', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 12px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="ma-table-container" style={{
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        <table className="ma-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Status</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Ticket Number</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Topic</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Reason</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Date Escalated</th>
              {statusFilter === 'resolved' && <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Date Resolved</th>}
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-badge" /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 4) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '65%' }} /></td>
                    {statusFilter === 'resolved' && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '55%' }} /></td>}
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : filteredCases.length === 0 ? (
              <tr>
                <td colSpan={6 + (statusFilter === 'resolved' ? 1 : 0)} style={{ textAlign: "center", padding: "48px", color: "#64748b" }}>
                  No escalated cases found
                </td>
              </tr>
            ) : (
              paginatedCases.map((c, index) => (
                <tr 
                  key={c.id} 
                  style={{ 
                    borderBottom: '1px solid #f1f5f9', 
                    transition: 'background 0.2s',
                    cursor: 'default'
                  }} 
                  onMouseEnter={(e) => e.target.style.background = '#f8fafc'} 
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <td style={{ padding: '16px' }}>
                    <span className={`ma-badge ${getStatusBadgeClass(c.escalationStatus)}`}>
                      {getStatusLabel(c.escalationStatus)}
                    </span>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#374151', maxWidth: '150px', wordBreak: 'break-word', lineHeight: '1.4' }}>{c.ticketNumber || "N/A"}</td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>{c.escalationTopic || "N/A"}</td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#374151' }}>{c.escalationReason || "N/A"}</td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#64748b' }}>{formatTimestamp(c.createdAt)}</td>
                  {statusFilter === 'resolved' && <td style={{ padding: '16px', fontSize: '14px', color: '#64748b' }}>{formatTimestamp(c.resolvedAt)}</td>}
                  <td style={{ padding: '16px' }}>
                    {c.escalationStatus === 'pending-review' && (
                      <button
                        type="button"
                        className="ma-action-btn approve"
                        onClick={() => setSelectedCase(c)}
                        aria-label="View"
                        onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; e.target.style.color = '#1d4ed8'; handleActionMouseEnter(e, "Review", 'view'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#1d4ed8'; handleActionMouseLeave(); }}
                        style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                      >
                        <FiEye size={16} />
                      </button>
                    )}
                    {c.escalationStatus === 'under-assessment' && (
                      <button
                        type="button"
                        className="ma-action-btn approve"
                        onClick={() => setSelectedCase(c)}
                        aria-label="Review Case"
                        onMouseEnter={(e) => { e.target.style.background = '#fde68a'; e.target.style.color = '#d97706'; handleActionMouseEnter(e, "Review Case", 'reschedule'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#fef3c7'; e.target.style.color = '#d97706'; handleActionMouseLeave(); }}
                        style={{ background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b', border: '1px solid', borderRadius: '12px' }}
                      >
                        <FiClipboard size={16} />
                      </button>
                    )}
                    {c.escalationStatus === 'resolved' && (
                      <button
                        type="button"
                        className="ma-action-btn approve"
                        onClick={() => setSelectedCase(c)}
                        aria-label="View Chat"
                        onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; e.target.style.color = '#1d4ed8'; handleActionMouseEnter(e, "View Chat", 'view'); }}
                        onMouseMove={handleActionMouseMove}
                        onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#1d4ed8'; handleActionMouseLeave(); }}
                        style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                      >
                        <FiEye size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportReport}
        isExporting={isExporting}
        activeTab="escalated cases"
      />

      {tooltip.visible && (
        <div
          className={`admin-action-tooltip ${tooltip.type}`}
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminMedicalEscalatedCases;
